<template>
  <div class="input-combo">
    <label
      v-if="label"
      :for="`${id}-input`"
      class="block text-sm font-semibold dark:text-white"
      :class="{ 'text-red-500': isError }"
    >
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <div
      class="input-combo__wrapper flex items-stretch w-full border border-gray-600 rounded overflow-hidden bg-white text-sm focus-within:ring focus-within:ring-blue-500 focus-within:border-blue-500 dark:bg-slate-900 dark:border-gray-700 transition-colors"
      :class="{ 'border-red-500 ring-red-500': hasError }"
    >
      <div :class="['input-combo__cell input-combo__cell--input', inputWrapperClass]">
        <InputDefault
          :id="`${id}-input`"
          v-model="inputModel"
          :type="inputType"
          :size="size"
          :placeholder="inputPlaceholder"
          :is-error="isError"
        />
      </div>

      <div class="w-px bg-gray-200 self-stretch" />

      <div :class="['input-combo__cell input-combo__cell--select', selectWrapperClass]">
        <SelectDefault
          v-if="selectType === 'default'"
          :id="`${id}-select`"
          v-model="selectModel"
          :options="options"
          :map-options="(mapOptions as ((item: object) => ISelectOption) | undefined)"
          :size="size"
          :placeholder="selectPlaceholder"
          :is-error="isError"
        />
        <SelectSearch
          v-else
          :id="`${id}-select`"
          v-model:value="selectModel"
          v-model:search="selectSearchText"
          :options="mergedSelectSearchOptions"
          :list="(options as object[])"
          :initial-data="true"
        />
      </div>
    </div>
    <p v-if="hasError && errorMessage" class="mt-1 text-xs text-red-500">{{ errorMessage }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import InputDefault from './InputDefault.vue'
import SelectDefault from './SelectDefault.vue'
import SelectSearch from './SelectSearch.vue'
import type { ISelectOption, ISelectSearchOptionsProps } from '@/modules/Core/interfaces/form/ISelect'

interface IInputComboProps {
  id: string
  label?: string
  modelValue: string | number | null | undefined
  selectValue: string | number | null | undefined
  options: unknown[]
  mapOptions?: (item: unknown) => ISelectOption
  selectType?: 'default' | 'search'
  selectSearchOptions?: ISelectSearchOptionsProps
  inputType?: 'text' | 'number'
  inputPlaceholder?: string
  selectPlaceholder?: string
  size?: 'small' | 'medium'
  inputWidth?: 'auto' | '1/3' | '2/5' | '1/2'
  required?: boolean
  isError?: boolean
  error?: string | Array<object> | null
}

const props = withDefaults(defineProps<IInputComboProps>(), {
  label: '',
  selectType: 'default',
  selectSearchOptions: () => ({}) as ISelectSearchOptionsProps,
  inputType: 'text',
  inputPlaceholder: '',
  selectPlaceholder: '',
  size: 'small',
  inputWidth: '2/5',
  required: false,
  isError: false,
  error: null,
  mapOptions: undefined
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null): void
  (e: 'update:selectValue', value: string | number | null): void
}>()

const inputModel = computed({
  get: () => props.modelValue ?? '',
  set: (v: string | number) => emit('update:modelValue', v === '' ? null : v)
})

const selectModel = computed({
  get: () => props.selectValue ?? null,
  set: (v: unknown) => emit('update:selectValue', (v as string | number | null) ?? null)
})

const selectSearchText = ref<string>('')

const resolveOptionText = (val: unknown): string => {
  if (val == null || val === '') return ''
  const list = Array.isArray(props.options) ? props.options : []
  const map =
    (props.selectSearchOptions && (props.selectSearchOptions as { mapOptions?: (item: object) => { optionText: string; optionValue: string | number } }).mapOptions) ||
    props.mapOptions
  if (!map) return ''
  for (const item of list) {
    try {
      const mapped = map(item as object)
      if (mapped && mapped.optionValue === val) return mapped.optionText ?? ''
    } catch {
      console.error('Error mapping option:', item)
    }
  }
  return ''
}

watch(
  () => props.selectValue,
  newVal => {
    if (props.selectType !== 'search') return
    if (newVal == null || newVal === '') {
      if (selectSearchText.value !== '') selectSearchText.value = ''
      return
    }
    const text = resolveOptionText(newVal)
    if (text && selectSearchText.value !== text) {
      selectSearchText.value = text
    }
  },
  { immediate: true }
)

const inputWrapperClass = computed(() => {
  const map: Record<string, string> = {
    auto: 'w-auto',
    '1/3': 'w-1/3',
    '2/5': 'w-2/5',
    '1/2': 'w-1/2'
  }
  return map[props.inputWidth] || 'w-2/5'
})

const selectWrapperClass = computed(() => 'flex-1 min-w-0')

const mergedSelectSearchOptions = computed((): ISelectSearchOptionsProps => {
  const base = (props.selectSearchOptions ?? {}) as ISelectSearchOptionsProps
  if (base.fieldFilter) return base
  const map = (base as { mapOptions?: (item: object) => ISelectOption }).mapOptions || props.mapOptions
  if (!map) return base
  return {
    ...base,
    fieldFilter: {
      name: '',
      mapOptions: (item: object) => {
        try {
          const mapped = map(item as object)
          return (mapped as { optionText?: string })?.optionText ?? ''
        } catch {
          return ''
        }
      }
    }
  }
})

const hasError = computed(() => props.isError || !!props.error)
const errorMessage = computed(() => {
  if (!props.error) return ''
  if (typeof props.error === 'string') return props.error
  if (Array.isArray(props.error) && props.error.length > 0) {
    const first = props.error[0] as { $message?: string }
    return first?.$message || ''
  }
  return ''
})
</script>

<style scoped>
.input-combo__cell :deep(input),
.input-combo__cell :deep(select) {
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  background: transparent;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.input-combo__cell :deep(input:focus),
.input-combo__cell :deep(select:focus) {
  outline: none !important;
  box-shadow: none !important;
  --tw-ring-shadow: 0 0 #0000 !important;
  --tw-ring-offset-shadow: 0 0 #0000 !important;
}

.input-combo__cell :deep(label) {
  display: none;
}

.input-combo__cell--select :deep(.relative > div[class*="border"]),
.input-combo__cell--select :deep([class*="rounded"]) {
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.input-combo__cell--select :deep(input) {
  padding-left: 2rem !important;
}
</style>
